import React from "react";
import { X, MapPin, Package, Boxes, Calendar, User, FileText, CheckCircle2, XCircle } from "lucide-react";

export default function StockLocationDetailModal({ isOpen, onClose, data }) {
  if (!isOpen || !data) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Intl.DateTimeFormat("id-ID", {
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(dateStr));
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden border border-slate-100 flex flex-col max-h-[90vh] animate-in fade-in zoom-in duration-200">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-teal-600 text-white shrink-0 rounded-t-2xl">
          <div className="flex items-center gap-2.5">
            <h2 className="font-bold text-base tracking-tight">Detail Lokasi Stok</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-5 text-xs overflow-y-auto flex-1">
          {/* Main Title & Status Badge */}
          <div className="flex items-start justify-between gap-4 p-4 bg-slate-50 rounded-xl border border-slate-100">
            <div className="flex items-center gap-3">
              {data.image_url && data.image_url.trim() !== "" ? (
                <div className="w-16 h-12 rounded-xl overflow-hidden border border-slate-200 shrink-0 bg-slate-100 flex items-center justify-center shadow-xs">
                  <img
                    src={data.image_url}
                    alt={data.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center shrink-0">
                  <MapPin className="w-6 h-6" />
                </div>
              )}
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Nama Lokasi Stok
                </span>
                <h3 className="text-lg font-bold text-slate-900 leading-snug">
                  {data.name}
                </h3>
                <span className="inline-flex px-2.5 py-0.5 rounded-full border text-[11px] font-semibold bg-emerald-50 text-emerald-700 border-emerald-200 mt-1">
                  {data.type || "Gudang"}
                </span>
              </div>
            </div>
            <div className="text-right">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Status
              </span>
              <div className="mt-1">
                {data.is_active ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                    Aktif
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-500 border border-slate-200">
                    <XCircle className="w-3.5 h-3.5 text-slate-400" />
                    Non-Aktif
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100">
              <div className="flex items-center gap-2 text-slate-500 mb-1">
                <Package className="w-4 h-4 text-teal-600" />
                <span className="font-semibold text-[11px]">Jumlah Produk Linked</span>
              </div>
              <p className="text-base font-bold text-slate-900">
                {data.product_count || 0} <span className="text-xs font-normal text-slate-500">Produk</span>
              </p>
            </div>

            <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100">
              <div className="flex items-center gap-2 text-slate-500 mb-1">
                <Boxes className="w-4 h-4 text-emerald-600" />
                <span className="font-semibold text-[11px]">Total Stok Tersimpan</span>
              </div>
              <p className="text-base font-bold text-emerald-600">
                {data.total_stock || 0}
              </p>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-slate-500 font-semibold">
              <FileText className="w-3.5 h-3.5 text-slate-400" />
              <span>Deskripsi / Catatan</span>
            </div>
            <p className="p-3 bg-slate-50 rounded-xl border border-slate-100 text-slate-700 leading-relaxed">
              {data.description || "Tidak ada deskripsi lokasi."}
            </p>
          </div>

          {/* Audit Metadata */}
          <div className="pt-3 border-t border-slate-100 grid grid-cols-2 gap-3 text-slate-500 text-[11px]">
            <div>
              <div className="flex items-center gap-1 font-medium text-slate-400 mb-0.5">
                <Calendar className="w-3 h-3" /> Dibuat Pada
              </div>
              <p className="font-semibold text-slate-700">{formatDate(data.created_at)}</p>
            </div>

            <div>
              <div className="flex items-center gap-1 font-medium text-slate-400 mb-0.5">
                <User className="w-3 h-3" /> Dibuat Oleh
              </div>
              <p className="font-semibold text-slate-700">{data.created_by_name || "-"}</p>
            </div>

            <div>
              <div className="flex items-center gap-1 font-medium text-slate-400 mb-0.5">
                <Calendar className="w-3 h-3" /> Terakhir Update
              </div>
              <p className="font-semibold text-slate-700">{formatDate(data.updated_at)}</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3.5 bg-slate-50 border-t border-slate-100 flex justify-end shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold transition">
            Tutup
          </button>
        </div>
      </div>
    </div>
  );
}
